import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { DevisForm, eur, dfr } from "@/pages/Devis";

const STATUS_CLS = {
  planifie: "bg-slate-100 text-slate-600", en_cours: "bg-emerald-100 text-emerald-700",
  en_pause: "bg-amber-100 text-amber-700", termine: "bg-blue-100 text-blue-700", annule: "bg-rose-100 text-rose-700",
};
const DEVIS_CLS = {
  draft: "bg-slate-100 text-slate-600", pending: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700", refused: "bg-rose-100 text-rose-700", expired: "bg-rose-50 text-rose-500",
};
const DEVIS_LBL = { draft: "Brouillon", pending: "En attente", accepted: "Accepté", refused: "Refusé", expired: "Expiré" };
const VISIT_LBL = { brouillon: "Brouillon", en_cours: "En cours", validee: "Validée", a_valider: "À valider" };

export default function SiteDetail() {
  const { id, sid } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [devis, setDevis] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("infos");
  const [devisForm, setDevisForm] = useState(null); // {initial, sourceFile}

  const load = useCallback(async () => {
    try {
      const s = await api.get(`/sites/${sid}`);
      setSite(s.data);
      const cid = s.data.client_id;
      const [dv, ws, dc] = await Promise.all([
        api.get("/devis", { params: { site_id: sid } }),
        api.get("/track/worksites", { params: { client_id: cid } }),
        api.get(`/sites/${sid}/documents`),
      ]);
      setDevis(dv.data || []);
      setWorksites((ws.data || []).filter((w) => w.site_id === sid));
      setDocs(dc.data || []);
    } catch (e) { toast.error(apiError(e)); navigate(`/clients/${id}`); }
  }, [sid, id, navigate]);

  useEffect(() => { load(); }, [load]);

  const downloadDoc = async (fid, name) => {
    try {
      const r = await api.get(`/track/files/${fid}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = name || "document"; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 8000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openNewDevis = () => setDevisForm({ initial: { client_id: site.client_id, site_id: sid }, sourceFile: null });
  const openDevisFromDoc = (doc) => setDevisForm({ initial: { client_id: site.client_id, site_id: sid, title: doc.name }, sourceFile: { id: doc.id, name: doc.name } });

  if (!site) return <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  const tabs = [
    ["infos", "Informations", "Info"],
    ["visites", `Visites (${site.visits?.length || 0})`, "ClipboardCheck"],
    ["devis", `Devis (${devis.length})`, "FileSignature"],
    ["interventions", `Interventions (${worksites.length})`, "Activity"],
    ["documents", `Documents (${docs.length})`, "FileText"],
  ];

  return (
    <div className="space-y-5" data-testid="site-detail-page">
      <button onClick={() => navigate(`/clients/${id}`)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="site-back">
        <Icons.ChevronLeft className="h-4 w-4" /> Retour au client
      </button>

      <Card className="p-5">
        {site.cover_file_id && (
          <img src={fileUrl(site.cover_file_id)} alt="" className="mb-4 h-44 w-full rounded-lg object-cover" data-testid="site-cover-img" onError={(e) => { e.target.style.display = "none"; }} />
        )}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">{site.name}</h1>
            <p className="mt-0.5 text-sm text-slate-500">{[site.address, site.commune].filter(Boolean).join(", ") || "Adresse non renseignée"}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(`/check?site=${sid}&client=${site.client_id}&new=1`)} data-testid="site-new-visit">
              <Icons.Plus className="mr-1.5 h-4 w-4" />Nouvelle visite
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={openNewDevis} data-testid="site-add-devis">
              <Icons.Plus className="mr-1.5 h-4 w-4" />Ajouter un devis
            </Button>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full flex-wrap justify-start gap-1" data-testid="site-tabs">
          {tabs.map(([k, lbl, ic]) => {
            const Ic = Icons[ic];
            return <TabsTrigger key={k} value={k} data-testid={`site-tab-${k}`} className="gap-1.5"><Ic className="h-4 w-4" />{lbl}</TabsTrigger>;
          })}
        </TabsList>

        {/* Informations */}
        <TabsContent value="infos" className="mt-4 space-y-3" data-testid="site-tabpanel-infos">
          <Card className="space-y-3 p-5 text-sm">
            <InfoRow icon="MapPin" label="Adresse" value={[site.address, site.commune].filter(Boolean).join(", ") || "—"} />
            <InfoRow icon="KeyRound" label="Instructions d'accès" value={site.access_instructions || "—"} />
            <div>
              <p className="mb-1 flex items-center gap-1.5 font-medium text-slate-700"><Icons.Users className="h-4 w-4 text-slate-400" />Contacts</p>
              {(site.contacts || []).length === 0 ? <p className="text-slate-400">Aucun contact.</p>
                : <div className="space-y-1.5">{site.contacts.map((c, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-slate-600">
                      <span className="font-medium text-slate-700">{c.name || "—"}{c.role ? ` · ${c.role}` : ""}</span>
                      {c.phone && <span className="flex items-center gap-1"><Icons.Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone}</span>}
                      {c.email && <span className="flex items-center gap-1"><Icons.Mail className="h-3.5 w-3.5 text-slate-400" />{c.email}</span>}
                    </div>
                  ))}</div>}
            </div>
          </Card>
        </TabsContent>

        {/* Visites */}
        <TabsContent value="visites" className="mt-4" data-testid="site-tabpanel-visites">
          {(site.visits?.length || 0) === 0 ? <Empty text="Aucune visite sur ce site." />
            : <div className="space-y-2">{site.visits.map((v) => (
                <Card key={v.id} className="flex cursor-pointer items-center justify-between gap-3 p-4 hover:shadow-md" onClick={() => navigate(`/check/${v.id}`)} data-testid={`site-visit-${v.id}`}>
                  <div><p className="font-semibold text-slate-800">{v.reference}</p><p className="text-sm text-slate-500">{dfr(v.date)}{v.prestations?.length ? ` · ${v.prestations.join(", ")}` : ""}</p></div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{VISIT_LBL[v.status] || v.status}</span>
                </Card>
              ))}</div>}
        </TabsContent>

        {/* Devis */}
        <TabsContent value="devis" className="mt-4" data-testid="site-tabpanel-devis">
          {devis.length === 0 ? <Empty text="Aucun devis pour ce site." action="Ajouter un devis" onAction={openNewDevis} />
            : <div className="space-y-2">{devis.map((d) => (
                <Card key={d.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`site-devis-${d.id}`}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${DEVIS_CLS[d.effective_status] || DEVIS_CLS.draft}`}>{DEVIS_LBL[d.effective_status] || d.effective_status}</span>
                      <span className="font-semibold text-slate-800">{d.title}</span>
                      <span className="text-xs text-slate-400">· {d.reference}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-slate-500">Émis le {dfr(d.issue_date)}</p>
                  </div>
                  <div className="font-display text-lg font-bold text-slate-900">{eur(d.amount_ttc)}</div>
                </Card>
              ))}</div>}
          <p className="mt-3 text-xs text-slate-400">Gérez la publication et l'acceptation des devis depuis la rubrique « Devis ».</p>
        </TabsContent>

        {/* Interventions */}
        <TabsContent value="interventions" className="mt-4" data-testid="site-tabpanel-interventions">
          {worksites.length === 0 ? <Empty text="Aucune intervention sur ce site." />
            : <div className="space-y-2">{worksites.map((w) => (
                <Card key={w.id} className="flex cursor-pointer items-center justify-between gap-3 p-4 hover:shadow-md" onClick={() => navigate(`/track/${w.id}`)} data-testid={`site-ws-${w.id}`}>
                  <div><p className="font-mono-data text-xs text-slate-400">{w.reference}</p><p className="font-semibold text-slate-800">{w.title}</p></div>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CLS[w.status] || "bg-slate-100 text-slate-600"}`}>{w.status_label || w.status}</span>
                </Card>
              ))}</div>}
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="mt-4" data-testid="site-tabpanel-documents">
          {docs.length === 0 ? <Empty text="Aucun document. Les fichiers déposés sur les chantiers de ce site apparaissent ici." />
            : <div className="space-y-2">{docs.map((doc) => (
                <Card key={doc.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`site-doc-${doc.id}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-500"><Icons.FileText className="h-5 w-5" /></span>
                    <div className="min-w-0"><p className="truncate font-medium text-slate-800">{doc.name}</p><p className="truncate text-xs text-slate-400">{doc.kind === "devis" ? "Devis" : "Document"}{doc.worksite_ref ? ` · ${doc.worksite_ref}` : ""}</p></div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => downloadDoc(doc.id, doc.name)} data-testid={`site-doc-dl-${doc.id}`}><Icons.Download className="mr-1.5 h-4 w-4" />Télécharger</Button>
                    <Button size="sm" variant="outline" onClick={() => openDevisFromDoc(doc)} data-testid={`site-doc-todevis-${doc.id}`}><Icons.FileSignature className="mr-1.5 h-4 w-4" />Créer un devis</Button>
                  </div>
                </Card>
              ))}</div>}
        </TabsContent>
      </Tabs>

      {devisForm && (
        <DevisForm initial={devisForm.initial} lockClientSite sourceFile={devisForm.sourceFile}
          onClose={() => setDevisForm(null)}
          onSaved={() => { setDevisForm(null); setTab("devis"); load(); }} />
      )}
    </div>
  );
}

const InfoRow = ({ icon, label, value }) => {
  const Ic = Icons[icon];
  return (
    <div className="flex items-start gap-2">
      <Ic className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div><p className="text-xs font-medium text-slate-500">{label}</p><p className="text-slate-700">{value}</p></div>
    </div>
  );
};

const Empty = ({ text, action, onAction }) => (
  <Card className="flex flex-col items-center gap-3 p-10 text-center text-sm text-slate-400">
    <span>{text}</span>
    {action && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={onAction}>{action}</Button>}
  </Card>
);
